import { Box } from '@air/zephyr';
import Image from 'next/image';

import { SectionData } from '~/lib/contentful/custom-types';
import { Container } from '~/old-marketing/components/Container';
import { Section } from '~/old-marketing/components/sections/Section';
import { SectionContent } from '~/old-marketing/components/sections/SectionContent';
import { SectionFieldTooltip } from '~/old-marketing/components/sections/SectionFieldTooltip';
import { SectionTitle } from '~/old-marketing/components/sections/SectionTitle';
import { worthlessStudiosTheme } from '~/old-marketing/components/sections/templates/worthless-studios/theme';

const TEMPLATE_KEY = 'worthless-studios-about';

export type WorthlessStudiosAboutSectionTemplateProps = Pick<SectionData, 'assets' | 'content' | 'id' | 'title'> & {
  isPreview?: boolean;
  isSubsection?: boolean;
};

export const WorthlessStudiosAboutSectionTemplate = ({
  assets,
  content,
  id,
  isPreview,
  isSubsection,
  title,
}: WorthlessStudiosAboutSectionTemplateProps) => {
  const film1 = assets?.[0];
  const film2 = assets?.[1];
  const film3 = assets?.[2];

  return (
    <Section
      className={worthlessStudiosTheme}
      css={{
        paddingTop: 286,
        paddingBottom: 60,
        overflow: 'visible',

        '@laptop': {
          paddingTop: 106,
        },
      }}
      id={id}
      templateKey={TEMPLATE_KEY}
    >
      <Container>
        <Box>
          <Box
            tx={{
              position: 'absolute',
              left: [-294, 'calc(clamp(0px, 26.25vw, 378px) * -1)'],
              top: [-80, '50%'],
              width: [588.6, 'clamp(0px, 65.2vw, 939px)'],
              transform: ['none', 'translateY(-50%)'],
            }}
          >
            <Box tx={{ position: 'relative', paddingBottom: '52.6%' }}>
              {film3 && (
                <SectionFieldTooltip isPreview={isPreview} field="Asset (2)">
                  <Box
                    tx={{
                      position: 'absolute',
                      top: '58.2%',
                      left: '23.2%',
                      width: '80%',
                      transform: 'rotate(6.77deg)',
                    }}
                  >
                    <Image
                      height={film3.file.details.image.height}
                      src={`http:${film3.file.url}`}
                      width={film3.file.details.image.width}
                    />
                  </Box>
                </SectionFieldTooltip>
              )}
              {film2 && (
                <SectionFieldTooltip isPreview={isPreview} field="Asset (2)">
                  <Box
                    tx={{
                      position: 'absolute',
                      top: '35.4%',
                      left: '21.2%',
                      width: '80%',
                      transform: 'rotate(-22.77deg)',
                    }}
                  >
                    <Image
                      height={film2.file.details.image.height}
                      src={`http:${film2.file.url}`}
                      width={film2.file.details.image.width}
                    />
                  </Box>
                </SectionFieldTooltip>
              )}
              {film1 && (
                <SectionFieldTooltip isPreview={isPreview} field="Asset (1)">
                  <Box
                    tx={{
                      position: 'absolute',
                      top: '22.8%',
                      left: '-2.2%',
                      width: '80%',
                      transform: 'rotate(-25.83deg)',
                    }}
                  >
                    <Image
                      height={film1.file.details.image.height}
                      src={`http:${film1.file.url}`}
                      width={film1.file.details.image.width}
                    />
                  </Box>
                </SectionFieldTooltip>
              )}
            </Box>
          </Box>
        </Box>

        <Box tx={{ width: ['100%', '58%'], marginLeft: [0, '42%'], paddingRight: [0, 'max(2.7vw, 40px)'] }}>
          {title && (
            <SectionTitle
              css={{
                marginBottom: 32,
                color: '$pigeon800',
                textAlign: 'left',

                '@laptop': {
                  textAlign: 'right',
                },
              }}
              isSubsection={isSubsection}
              size="heading-48"
            >
              {title}
            </SectionTitle>
          )}

          {content && (
            <SectionContent
              content={content}
              css={{
                color: '$pigeon600',
                letterSpacing: 0.05,
                lineHeight: 1.6,
                textAlign: 'left',

                '@laptop': {
                  textAlign: 'right',
                },
              }}
            />
          )}
        </Box>
      </Container>
    </Section>
  );
};

WorthlessStudiosAboutSectionTemplate.templateKey = TEMPLATE_KEY;
